// Phase 31 Plan 31-04 Task 1 — AffinityChipRow.
//
// 4-chip row docked above MatchBottomBar showing the caller's 4 secret
// affinity letters. Chips are inert in v1 (UI-SPEC §3.7) and pulse Sun-Yellow
// for 600ms when the caller's most-recent claimed word includes an affinity
// letter (UI-SPEC §5 row 4 + plan 31-04 <behavior>). Mounts the
// KnownOpponentRunesRow sub-component only when the caller has played Quill
// in Read mode (your_read_revealed_letters.length === 2).
//
// CRITICAL — client-side B12 firewall hold (NY-08-10 / T-31-01): this
// component reads state EXCLUSIVELY from the playerView prop. It NEVER
// inspects opponent_public, never falls back to global state, never reads
// URL parameters. The single trust boundary is the prop the parent
// (MatchScreen) hands down. Caller-only fields consumed:
//   - your_affinity_letters (length-4 tuple — caller-only by construction in
//     PlayerViewSchema)
//   - your_read_revealed_letters (length-0|2 tuple — caller-only)
// Zero references to opponent.*; web-affinity-firewall.test.ts grep gate
// enforces this in CI.
//
// Pulse trigger contract (UI-SPEC §5 reduced-motion table):
// - Standard: 600ms total — border-width swap 2 → 3px + border-color swap
//   surface-stroke → cta-primary. CSS handles the easing via a 200ms
//   transition on the border properties.
// - Reduced motion: 100ms linear border-alpha cross-in, 400ms hold, 100ms
//   cross-out. We model this with the same data-attribute toggle + a CSS
//   transition narrowed to 100ms (the data-affinity-reduced-motion attribute
//   parks the per-chip styling on the linear path; no scale, no transform).
// - Multiple hits: pulse multiple chips simultaneously (no stagger — Pitfall
//   9 lock from 31-RESEARCH).
//
// SR contract: every affinity hit fires a polite announcement via
// `match.affinity.sr_pulse` ("Your affinity rune {letter} matched.") through
// the existing useAnnounce primitive.

import { useStore } from '@nanostores/react'
import { type JSX, useEffect, useRef, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'

import { playSfx } from '../../game/audio/useAudio'
import type { PlayerView } from '../../game/PlayerViewSchema'
import { $reducedMotion } from '../../stores/motion'
import { useAnnounce } from '../components/useAnnounce'
import { KnownOpponentRunesRow } from './KnownOpponentRunesRow'

export interface AffinityChipRowProps {
  readonly playerView: PlayerView
  /**
   * Cells consumed in the most recent claimed word. Threaded down from
   * MatchScreen via the existing TurnResponse handler — when the value
   * changes (new word claim), the pulse useEffect compares each cell's
   * letter against the caller's affinity tuple and lights up matching chips
   * for 600ms.
   *
   * Optional: pre-T1 / pre-trace, no word has been claimed yet — the row
   * still renders, just with no chips pulsing.
   */
  readonly lastClaimedWordCells?: ReadonlyArray<PlayerView['board'][number]>
}

const PULSE_DURATION_MS = 600

export function AffinityChipRow({
  playerView,
  lastClaimedWordCells,
}: AffinityChipRowProps): JSX.Element {
  const intl = useIntl()
  const reducedMotion = useStore($reducedMotion)
  const announce = useAnnounce()
  const [pulsingIndices, setPulsingIndices] = useState<ReadonlySet<number>>(
    () => new Set<number>(),
  )

  const affinityLetters = playerView.your_affinity_letters
  const readRevealed = playerView.your_read_revealed_letters

  // Refs to current `announce` + `intl` so the pulse effect can call them
  // without inflating its dependency array. `useAnnounce()` returns a fresh
  // closure every render (no internal useCallback) and `useIntl()` likewise
  // returns a fresh object — including either in the effect's dep list would
  // re-fire the effect on EVERY render and (because setPulsingIndices with a
  // fresh Set never passes Object.is) trigger an infinite render loop.
  const announceRef = useRef(announce)
  const intlRef = useRef(intl)
  announceRef.current = announce
  intlRef.current = intl

  useEffect(() => {
    if (!lastClaimedWordCells || lastClaimedWordCells.length === 0) {
      return undefined
    }
    const hits = new Set<number>()
    for (const cell of lastClaimedWordCells) {
      if (cell.letter === null) continue
      const idx = affinityLetters.indexOf(cell.letter)
      if (idx >= 0) hits.add(idx)
    }
    if (hits.size === 0) return undefined
    setPulsingIndices(hits)
    // §10.9 — soft warm ping when an affinity rune matches (chips pulse
    // Sun-Yellow in parallel). One cue per claim regardless of hit count.
    playSfx('affinity-ping')
    // SR announce — one polite announcement per matched letter. useAnnounce's
    // 100ms debounce coalesces rapid-fire so two near-simultaneous hits emit
    // the last one (acceptable per WCAG 2.3 — the visible pulse covers the
    // multi-hit case visually).
    for (const idx of hits) {
      const letter = affinityLetters[idx]
      if (typeof letter === 'string' && letter.length > 0) {
        announceRef.current(
          intlRef.current.formatMessage(
            // AFFINITY-07 — the SR cue states the payoff so a newcomer learns
            // WHY the rune matters, not just that it "matched".
            { id: 'match.affinity.sr_pulse_v2' },
            { letter: letter.toUpperCase() },
          ),
          'polite',
        )
      }
    }
    const handle = window.setTimeout(() => {
      setPulsingIndices(new Set<number>())
    }, PULSE_DURATION_MS)
    return () => {
      window.clearTimeout(handle)
    }
  }, [lastClaimedWordCells, affinityLetters])

  return (
    <section
      data-screen-region="affinity-chip-row"
      aria-label={intl.formatMessage({ id: 'match.affinity.your_runes_label' })}
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--space-2)',
        padding: 'var(--space-2) var(--space-4)',
      }}
    >
      <span
        style={{
          fontFamily: 'var(--font-family-display)',
          fontWeight: 700,
          fontSize: 'var(--font-size-16)',
          lineHeight: 1.2,
          color: 'var(--color-text-primary)',
        }}
      >
        <FormattedMessage id="match.affinity.your_runes_label" />
      </span>
      {/* AFFINITY-03 — permanent caption stating the payoff. The 4 chips alone
          never told a new player WHY these letters matter; this one line does,
          and it stays visible whether or not the tutorial was played. */}
      <span
        data-affinity-caption
        style={{
          fontFamily: 'var(--font-family-body)',
          fontWeight: 600,
          fontSize: 'var(--font-size-12)',
          lineHeight: 1.4,
          color: 'var(--color-text-secondary)',
        }}
      >
        <FormattedMessage id="match.affinity.your_runes_caption" />
      </span>
      <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
        {affinityLetters.map((letter, idx) => (
          <AffinityChip
            // Composite key: letter + idx keeps stability under duplicate
            // letters in the tuple (server allows e.g. ['Q','Q','U','I']
            // post-Mark mutations); a letter-only key would collide and a
            // pure-idx key drops affordance signal.
            // biome-ignore lint/suspicious/noArrayIndexKey: composite key with stable letter prefix; bounded length-4 tuple
            key={`affinity-chip-${letter}-${idx}`}
            letter={letter}
            pulsing={pulsingIndices.has(idx)}
            reducedMotion={reducedMotion}
          />
        ))}
      </div>
      {readRevealed.length === 2 ? (
        <KnownOpponentRunesRow
          letters={readRevealed as readonly [string, string]}
        />
      ) : null}
    </section>
  )
}

interface AffinityChipProps {
  readonly letter: string
  readonly pulsing: boolean
  readonly reducedMotion: boolean
}

function AffinityChip({
  letter,
  pulsing,
  reducedMotion,
}: AffinityChipProps): JSX.Element {
  return (
    <button
      type="button"
      aria-disabled="true"
      tabIndex={-1}
      data-affinity-letter={letter}
      data-affinity-pulsing={pulsing ? 'true' : 'false'}
      data-affinity-reduced-motion={reducedMotion ? 'true' : 'false'}
      style={{
        width: 36,
        height: 36,
        borderRadius: 'var(--radius-md)',
        background: 'var(--color-surface-mid)',
        border: pulsing
          ? '3px solid var(--color-cta-primary)'
          : '2px solid var(--color-surface-stroke)',
        color: 'var(--color-inkwell)',
        fontFamily: 'var(--font-family-display)',
        fontWeight: 700,
        fontSize: 'var(--font-size-22)',
        lineHeight: 1,
        cursor: 'default',
        padding: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        // Reduced motion: 100ms linear cross — fast border-color/width swap
        // with no scale/transform (UI-SPEC §5 row 4 fallback).
        // Standard motion: 200ms ease-out on border — the 600ms pulse total
        // is achieved by the data-affinity-pulsing flip being held by the
        // parent for 600ms; the ease handles the 2-state interpolation.
        transition: reducedMotion
          ? 'border-color 100ms linear, border-width 100ms linear'
          : 'border-color 200ms ease-out, border-width 200ms ease-out',
      }}
    >
      {letter.toUpperCase()}
    </button>
  )
}
